import {
  ChangeEventHandler,
  FC,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Portal } from "react-portal";

import { clsxMerge } from "shared/lib/helpers/styles";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";

import { CloseIcon } from "../icons";

type ModalTriggerRef = MutableRefObject<HTMLInputElement>;

export interface ModalChildrenFnPropsI {
  modalTriggerRef: ModalTriggerRef;
  modalManager: typeof modalHelpers;
  handleClose: () => void;
  setModalTitle: (title: ReactNode) => void;
  setModalDescription: (description: ReactNode) => void;
}

export interface ModalPropsI {
  id: string;

  onOpen?: () => void;
  onClose?: (isInitiatedByUser?: boolean) => void;

  unmountOnClose?: boolean;
  showCloseButton?: boolean;
  shouldCloseOnOverlayClick?: boolean;

  titleImage?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode | FC<ModalChildrenFnPropsI>;

  // Supported classNames
  titleImageClassName?: string;
  modalContainerClassName?: string;
  modalBoxClassName?: string;
  closeButtonClassName?: string;
  closeIconClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
}

const UNMOUNT_MODAL_CONTENT_TIMEOUT_MS = 500;

const Modal: FC<ModalPropsI> = ({
  id: modalId,
  onOpen,
  onClose,
  titleImage,
  title,
  description,
  unmountOnClose = true,
  showCloseButton = true,
  shouldCloseOnOverlayClick = true,
  children,

  titleImageClassName,
  modalContainerClassName,
  modalBoxClassName,
  closeButtonClassName,
  closeIconClassName,
  titleClassName,
  descriptionClassName,
}) => {
  const modalTriggerRef = useRef() as ModalTriggerRef;
  const [isVisible, setIsVisible] = useState(false);

  const [titleState, setTitleState] = useState<ReactNode>(title);
  const [descriptionState, setDescriptionState] =
    useState<ReactNode>(description);

  const modalTitle = titleState || title;
  const modalDescription = descriptionState || description;

  const handleClose = (initiatedByUser = false) => {
    modalHelpers.close(modalId);

    onClose?.(initiatedByUser);

    if (unmountOnClose) {
      setTimeout(() => {
        setIsVisible(false);
      }, UNMOUNT_MODAL_CONTENT_TIMEOUT_MS);
    }
  };

  const modalTriggerChangeHandler: ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (e.target.checked) {
      onOpen?.();

      if (!isVisible && unmountOnClose) {
        setIsVisible(true);
      }
    } else {
      onClose?.();
    }
  };

  const handleClickOnTheOutsideModalArea = (e: any) => {
    if (
      e.target.attributes.getNamedItem("data-target")?.value ===
        "outside-modal-area" &&
      shouldCloseOnOverlayClick
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    setTitleState(title);
  }, [title]);

  useEffect(() => {
    setDescriptionState(description);
  }, [description]);

  return (
    // @ts-ignore
    <Portal>
      <input
        type="checkbox"
        id={modalId}
        className="modal-toggle"
        ref={modalTriggerRef}
        onChange={modalTriggerChangeHandler}
      />

      <div
        data-target="outside-modal-area"
        className={clsxMerge(
          "modal",
          shouldCloseOnOverlayClick && "cursor-pointer"
        )}
        onClick={handleClickOnTheOutsideModalArea}
      >
        <div
          className={clsxMerge(
            "flex cursor-default justify-center",
            modalContainerClassName
          )}
        >
          <div
            className={clsxMerge(
              "modal-box relative rounded-[20px] p-6",
              "w-[600px] max-w-none",
              modalBoxClassName
            )}
          >
            {showCloseButton && (
              <button
                onClick={() => handleClose(true)}
                className={clsxMerge(
                  "btn-ghost absolute right-6 top-4",
                  "z-10 rounded-full p-1",
                  closeButtonClassName
                )}
              >
                <CloseIcon
                  className={clsxMerge("h-6 w-6", closeIconClassName)}
                />
              </button>
            )}

            {titleImage && (
              <div className={clsxMerge("mb-3 w-full", titleImageClassName)}>
                {titleImage}
              </div>
            )}

            {modalTitle && (
              <h3 className={clsxMerge("typography-h2 mb-3", titleClassName)}>
                {modalTitle}
              </h3>
            )}

            {modalDescription && (
              <p
                className={clsxMerge(
                  "typography-body mb-5 text-black/60",
                  descriptionClassName
                )}
              >
                {modalDescription}
              </p>
            )}

            {isVisible && (
              <>
                {typeof children === "function"
                  ? children({
                      handleClose,
                      modalTriggerRef,
                      modalManager: modalHelpers,
                      setModalTitle: setTitleState,
                      setModalDescription: setDescriptionState,
                    })
                  : children}
              </>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default Modal;
